import { createSelector } from 'reselect';

import { SelectedSeat } from '../types';

import { getState } from '../rootSelector';
import { getSelectedSegmentId } from '../selectedSegmentId/selectors';
import { getSelectedSegmentRows } from '../seatMap/selectors';

import { SelectedSeatWithPrice } from '../../../../entities/seat-map/model/types';

import { getSeatPrice } from '../../../../../SeatMap/utils';

export const getSelectedSeatsState = createSelector(getState, state => state.selectedSeats);

export const getIsAnySeatSelected = createSelector(getSelectedSeatsState, seats => !!Object.keys(seats).length);

export const getSelectedSeatsBySelectedSegmentId = createSelector(
	getSelectedSeatsState,
	getSelectedSegmentId,
	(selectedSeats, selectedSegmentId) => selectedSeats[selectedSegmentId] ?? {}
);

export const getSelectedSeatsWithPricesBySelectedSegmentId = createSelector(
	getSelectedSeatsBySelectedSegmentId,
	getSelectedSegmentRows,
	(selectedSeats, selectedSegmentRows) => {
		const seatsWithPrice: { [passengerId: string]: SelectedSeatWithPrice[] } = {};

		// Создаем карту для быстрого поиска мест
		const seatMap = new Map<string, any>();
		selectedSegmentRows.forEach(row => {
			row.seats?.forEach(seat => {
				seatMap.set(seat.number, seat);
			});
		});

		Object.entries(selectedSeats).forEach(([passengerId, seats]) => {
			if (seats?.length) {
				seatsWithPrice[passengerId] = seats.map(selectedSeat => {
					const seatNumber = `${selectedSeat.row}${selectedSeat.letter}`;
					const seatOnSegment = seatMap.get(seatNumber);

					return {
						...selectedSeat,
						price: getSeatPrice(seatOnSegment)
					};
				});
			}
		});

		return seatsWithPrice;
	}
);

export const getSelectedSeats = createSelector(getSelectedSeatsState, selectedSeats => {
	const result: SelectedSeat[] = [];

	if (Object.keys(selectedSeats)?.length) {
		for (const segmentId in selectedSeats) {
			for (const passengerId in selectedSeats[segmentId]) {
				const seats = selectedSeats[segmentId]?.[passengerId] ?? [];

				if (seats.length) {
					result.push(...seats);
				}
			}
		}
	}

	return result;
});

export const getSelectedSeatsArrayBySelectedSegmentId = createSelector(
	getSelectedSeatsBySelectedSegmentId,
	selectedSeatsBySelectedSegmentId => {
		if (!Object.keys(selectedSeatsBySelectedSegmentId)?.length) {
			return [];
		}

		const selectedSeatNumbers: SelectedSeat[] = [];

		for (const passengerId in selectedSeatsBySelectedSegmentId) {
			const selectedSeats = selectedSeatsBySelectedSegmentId[passengerId];

			if (selectedSeats?.length) {
				selectedSeatNumbers.push(...selectedSeats);
			}
		}

		return selectedSeatNumbers;
	}
);

export const getSelectedSeatBySeatNumber = (number: string) =>
	createSelector(getSelectedSeatsArrayBySelectedSegmentId, selectedSeatsArrayBySelectedSegmentId =>
		selectedSeatsArrayBySelectedSegmentId.find(
			selectedSeat => `${selectedSeat.row}${selectedSeat.letter}` === number
		)
	);
